import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Table, Spinner, Alert, Badge, Button, Container, Row, Col } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import apiService from '../services/apiService';
import Breadcrumb from '../components/Breadcrumb';

// Get variant for log level badges
const getBadgeVariant = (level) => {
  switch (level) {
    case 'ERROR':
      return 'danger';
    case 'WARNING':
      return 'warning';
    case 'INFO':
      return 'info';
    case 'DEBUG':
      return 'secondary';
    default:
      return 'light';
  }
};

// Function to format datetime with nanoseconds
const formatDateTimeWithNanos = (dateString) => {
    if (!dateString) return null;
    
    try {
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        const milliseconds = String(date.getMilliseconds()).padStart(3, '0');
        
        // Extract nanoseconds from the original string if available
        const nanoMatch = dateString.match(/\.(\d{3,9})/);
        let nanos = '000000';
        if (nanoMatch) {
            const fullNanos = nanoMatch[1].padEnd(9, '0');
            nanos = fullNanos.substring(3, 9); // Get microseconds + nanoseconds
        }
        
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}.${milliseconds}${nanos}`;
    } catch (error) {
        return dateString;
    }
};

// Get log level icon
const getLogLevelIcon = (level) => {
  switch (level) {
    case 'ERROR':
      return '🔴';
    case 'WARNING':
      return '🟡';
    case 'INFO':
      return '🔵';
    case 'DEBUG':
      return '🟣';
    default:
      return '⚪';
  }
};

const AutomationLogs = () => {
    const { automationRunId } = useParams();
    const navigate = useNavigate();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [automationRun, setAutomationRun] = useState(null);
    const [wsConnection, setWsConnection] = useState(null);

    // // Function to fetch automation run details
    // const fetchAutomationRun = useCallback(async () => {
    //     try {
    //         const data = await apiService.getAutomationRun(automationRunId);
    //         setAutomationRun(data);
    //     } catch (err) {
    //         console.error('Error fetching automation run:', err);
    //     }
    // }, [automationRunId]);

    // Function to fetch logs
    const fetchLogs = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await apiService.getAutomationRunLogs(automationRunId);
            const logs = data.results || data;
            // Sort logs by timestamp (latest first)
            const sortedLogs = logs;
            setLogs(sortedLogs);
        } catch (err) {
            setError('Failed to fetch automation logs.');
            console.error('Error fetching automation logs:', err);
        } finally {
            setLoading(false);
        }
    }, [automationRunId]);

    // Handle real-time log updates
    const handleLogUpdate = useCallback((newLog) => {
        console.log({newLog})
        setLogs(prevLogs => {
            // Check if log already exists to prevent duplicates
            const logExists = prevLogs.some(log => 
                log.id === newLog.id || 
                (log.timestamp === newLog.timestamp && log.message === newLog.message)
            );
            
            if (logExists) {
                return prevLogs;
            }
            
            // Add new log and sort by timestamp (latest first)
            const updatedLogs = [newLog, ...prevLogs];
            return updatedLogs;
        });
    }, []);

    // Setup WebSocket connection for real-time log updates
    useEffect(() => {
        const setupWebSocket = () => {
            const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
            const wsUrl = `wss://app-spire-abstract-gbkpu.ondigitalocean.app/ws/automation/logs/${automationRunId}/`;
            
            const ws = new WebSocket(wsUrl);
            
            ws.onopen = () => {
                console.log('WebSocket connected for automation logs');
                // Send initial message to get logs
                ws.send(JSON.stringify({
                    type: 'get_logs'
                }));
            };
            
            ws.onmessage = (event) => {
                const data = JSON.parse(event.data);
                console.log({data})
                if (data.type === 'log_update') {
                    if(data.log) handleLogUpdate(data.log);
                } else if (data.type === 'logs_list') {
                    // // Sort logs by timestamp (latest first) when receiving initial list
                    // const sortedLogs = data.logs
                    // setLogs(sortedLogs);
                    // setLoading(false);
                }
            };
            
            ws.onclose = () => {
                console.log('WebSocket disconnected for automation logs');
                // Attempt to reconnect after 3 seconds
                setTimeout(setupWebSocket, 3000);
            };
            
            ws.onerror = (error) => {
                console.error('WebSocket error:', error);
            };
            
            setWsConnection(ws);
        };

        setupWebSocket();
        
        // Cleanup function
        return () => {
            if (wsConnection) {
                wsConnection.close();
            }
        };
    }, [automationRunId, handleLogUpdate]);

    // Initial data fetch
    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    // Render professional step badge with enhanced styling
    const renderStepBadge = (stepNumber) => {
        return (
            <div 
                className="step-badge-professional"
                style={{
                    background: 'linear-gradient(135deg, #007bff 0%, #0056b3 100%)',
                    boxShadow: '0 4px 15px rgba(0, 123, 255, 0.3)',
                    color: 'white',
                    padding: '0px 1.6rem',
                    borderRadius: '10%',
                    fontSize: '0.8rem',
                    fontWeight: '700',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none',
                    transition: 'all 0.3s ease',
                    cursor: 'default',
                    minWidth: '32px',
                    minHeight: '32px',
                    width: '32px',
                    height: '32px'
                }}
            >
                <span>{stepNumber}</span>
            </div>
        );
    };

    // Format timestamp
    const formatTimestamp = (timestamp) => {
        return new Date(timestamp).toLocaleString();
    };

    // Go back to automation runs
    const handleGoBack = () => {
        navigate('/automation-runs');
    };

    const breadcrumbItems = [
        { label: 'Agent Copies',  },
        { label: 'Closing Coordinator', href: '/automation-runs' },
        { label: `Automation Logs - Run #${automationRunId}`, href: null }
    ];

    return (
        <div className="main-container">
            <Container fluid className="py-4">
                <Breadcrumb items={breadcrumbItems} />
                <Row>
                    <Col>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <div>
                                <h2 className="page-title mb-1">Automation Logs</h2>
                                <p className="page-subtitle text-muted mb-0">
                                    Detailed logs for automation trigger run: <span className="fw-bold text-primary">{automationRunId}</span>
                                </p>
                            </div>
                            <Button 
                                variant="outline-primary" 
                                onClick={() => navigate('/automation-runs')}
                                className="back-button"
                            >
                                ← Back
                            </Button>
                        </div>

                        {error && (
                            <Alert variant="danger" className="modern-alert">
                                <i className="fas fa-exclamation-triangle me-2"></i>
                                {error}
                            </Alert>
                        )}

                        <Card className="modern-card shadow-sm">
                            <Card.Header className="modern-card-header">
                                <div className="d-flex align-items-center">
                                    
                                    <div>
                                        <h5 className="mb-0">Log Entries</h5>
                                       
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body className="p-0">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" role="status" className="loading-spinner">
                                            <span className="visually-hidden">Loading...</span>
                                        </Spinner>
                                        <p className="mt-3 text-muted">Loading logs...</p>
                                    </div>
                                ) : logs.length === 0 ? (
                                    <div className="empty-state py-5 text-center">
                                        <div className="empty-icon mb-3">📝</div>
                                        <h5 className="text-muted">No logs found</h5>
                                        <p className="text-muted mb-0">No log entries were found for this automation run.</p>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <Table hover className="table-modern mb-0">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '280px' }}>Date & Time</th>
                                                    <th style={{ width: '120px' }}>Steps</th>
                                                    <th>Message</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {logs.map((log, index) => (
                                                    <tr key={index}>
                                                        <td className="date-cell">
                                                            {formatDateTimeWithNanos(log.timestamp)}
                                                        </td>
                                                        <td>{renderStepBadge(index)}</td>
                                                        <td className="message-cell">
                                                            <div className="log-message">
                                                                {log.message}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default AutomationLogs;

// Professional styling to match AutomationRuns.js
const styles = `
<style>
.main-container {
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    min-height: 100vh;
    overflow-x: hidden !important;
}

.page-title {
    color: #2c3e50;
    font-weight: 700;
    font-size: 2rem;
    text-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.page-subtitle {
    font-size: 1rem;
    color: #6c757d;
}

.back-button {
    border-radius: 8px;
    padding: 0.5rem 1rem;
    font-weight: 600;
    transition: all 0.3s ease;
    border: 2px solid #007bff;
}

.back-button:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 123, 255, 0.3);
}

.modern-alert {
    border: none;
    border-radius: 12px;
    box-shadow: 0 4px 15px rgba(220, 53, 69, 0.2);
    border-left: 4px solid #dc3545;
}

.modern-card {
    border: none;
    border-radius: 16px;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(10px);
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.modern-card-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    border: none;
    padding: 1.5rem;
}

.header-icon {
    font-size: 1.5rem;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    width: 50px;
    height: 50px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.loading-spinner {
    color: #007bff;
    width: 3rem;
    height: 3rem;
}

.empty-state {
    color: #6c757d;
}

.empty-icon {
    font-size: 3rem;
    opacity: 0.5;
}

.table-responsive {
    overflow-x: hidden !important;
}

.table-modern {
    table-layout: auto;
    width: 100%;
    margin: 0;
}

.table-modern thead th {
    background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
    color: #495057;
    font-weight: 600;
    text-transform: uppercase;
    font-size: 0.75rem;
    letter-spacing: 0.5px;
    padding: 1rem 0.8rem;
    border: none;
    position: sticky;
    top: 0;
    z-index: 10;
}

.table-modern tbody td {
    padding: 1rem 0.8rem;
    border: none;
    border-bottom: 1px solid #f1f3f4;
    vertical-align: middle;
    word-wrap: break-word;
    max-width: 0;
}

.table-modern tbody tr {
    transition: all 0.2s ease;
}

.table-modern tbody tr:hover {
    background-color: rgba(0, 123, 255, 0.05);
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.date-cell {
    font-size: 0.75rem;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    color: #6c757d;
    white-space: nowrap;
    min-width: 280px;
    width: 280px;
}

.message-cell {
    font-size: 0.85rem;
    line-height: 1.4;
}

.log-message {
    word-wrap: break-word;
    overflow-wrap: break-word;
    hyphens: auto;
    max-width: 100%;
}

.step-badge-professional:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(0, 123, 255, 0.4) !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
    .page-title {
        font-size: 1.5rem;
    }
    
    .page-subtitle {
        font-size: 0.9rem;
    }
    
    .table-modern thead th {
        padding: 0.75rem 0.5rem;
        font-size: 0.7rem;
    }
    
    .table-modern tbody td {
        padding: 0.75rem 0.5rem;
        font-size: 0.8rem;
    }
    
    .date-cell {
        font-size: 0.7rem;
        max-width: 140px;
    }
    
    .step-badge-professional {
        font-size: 0.7rem !important;
        min-width: 28px !important;
        min-height: 28px !important;
        width: 28px !important;
        height: 28px !important;
    }
}

@media (max-width: 576px) {
    .main-container {
        padding: 0.5rem;
    }
    
    .modern-card-header {
        padding: 1rem;
    }
    
    .header-icon {
        width: 40px;
        height: 40px;
        font-size: 1.2rem;
    }
    
    .table-modern thead th {
        padding: 0.5rem 0.3rem;
    }
    
    .table-modern tbody td {
        padding: 0.5rem 0.3rem;
    }
}
</style>
`;

// Inject styles
if (typeof document !== 'undefined') {
    const styleElement = document.createElement('div');
    styleElement.innerHTML = styles;
    document.head.appendChild(styleElement);
}
